import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { WifiIcon, PhoneIcon, TvIcon, MonitorSmartphoneIcon, UsersIcon, CheckCircle2Icon, ArrowRightIcon } from 'lucide-react';
export function QuoteForm() {
  const [step, setStep] = useState(1);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    zipCode: '',
    services: [] as string[],
    users: '',
    fullName: '',
    organization: '',
    email: '',
    phone: '',
    additionalInfo: ''
  });
  const handleServiceToggle = (service: string) => {
    setFormData(prev => ({
      ...prev,
      services: prev.services.includes(service) ? prev.services.filter(s => s !== service) : [...prev.services, service]
    }));
  };
  const handleNext = () => setStep(prev => Math.min(prev + 1, 4));
  const handleBack = () => setStep(prev => Math.max(prev - 1, 1));
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitted(true);
  };
  const renderStepIndicators = () => {
    return <div className="mb-8" data-id="element-301">
        <div className="flex justify-between items-center relative" data-id="element-302">
          <div className="absolute left-0 top-1/2 transform -translate-y-1/2 w-full h-1 bg-slate-100 rounded-full z-0" data-id="element-303"></div>
          <div className="absolute left-0 top-1/2 transform -translate-y-1/2 h-1 bg-primary rounded-full z-0 transition-all duration-300" style={{
          width: `${(step - 1) / 3 * 100}%`
        }} data-id="element-304"></div>

          {[1, 2, 3, 4].map(num => <div key={num} className={`relative z-10 flex items-center justify-center w-8 h-8 rounded-full text-sm font-bold transition-colors duration-300 ${step >= num ? 'bg-primary text-white' : 'bg-white text-slate-400 border-2 border-slate-200'}`} data-id="element-305">
              {step > num ? <CheckCircle2Icon className="w-5 h-5" data-id="element-306" /> : num}
            </div>)}
        </div>
        <div className="flex justify-between mt-2 text-xs font-medium text-slate-500" data-id="element-307">
          <span data-id="element-308">Location</span>
          <span className="ml-2" data-id="element-309">Services</span>
          <span className="ml-4" data-id="element-310">Size</span>
          <span data-id="element-311">Details</span>
        </div>
      </div>;
  };
  if (isSubmitted) {
    return <div className="bg-white rounded-2xl shadow-xl p-8 w-full max-w-lg mx-auto text-center border border-slate-100" data-id="element-312">
        <div className="w-16 h-16 bg-green-100 text-green-600 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-313">
          <CheckCircle2Icon className="w-8 h-8" data-id="element-314" />
        </div>
        <h3 className="text-2xl font-bold text-slate-900 mb-2" data-id="element-315">
          Quote Request Received!
        </h3>
        <p className="text-slate-600 mb-8" data-id="element-316">
          Thank you for reaching out. One of our telecom specialists will review
          your requirements and contact you within 30 minutes.
        </p>
        <button onClick={() => {
        setIsSubmitted(false);
        setStep(1);
        setFormData({
          zipCode: '',
          services: [],
          users: '',
          fullName: '',
          organization: '',
          email: '',
          phone: '',
          additionalInfo: ''
        });
      }} className="bg-primary hover:bg-primary-dark text-white px-8 py-3 rounded-full font-medium transition-colors" data-id="element-317">
          Submit Another Request
        </button>
      </div>;
  }
  return <div className="bg-white rounded-2xl shadow-2xl p-6 md:p-8 w-full max-w-lg mx-auto border border-slate-100" id="quote" data-id="element-318">
      <h3 className="text-2xl font-bold text-slate-900 mb-6 text-center" data-id="element-319">
        Get A Quote
      </h3>

      {renderStepIndicators()}

      <form onSubmit={handleSubmit} className="min-h-[300px] flex flex-col" data-id="element-320">
        <AnimatePresence mode="wait" data-id="element-321">
          <motion.div key={step} initial={{
          opacity: 0,
          x: 20
        }} animate={{
          opacity: 1,
          x: 0
        }} exit={{
          opacity: 0,
          x: -20
        }} transition={{
          duration: 0.2
        }} className="flex-grow" data-id="element-322">
            {/* STEP 1 */}
            {step === 1 && <div className="space-y-4" data-id="element-323">
                <h4 className="text-lg font-semibold text-slate-800 mb-4" data-id="element-324">
                  Where is your business located?
                </h4>
                <div data-id="element-325">
                  <label htmlFor="zipCode" className="block text-sm font-medium text-slate-700 mb-1" data-id="element-326">
                    Your Zip Code
                  </label>
                  <input type="text" id="zipCode" className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:ring-2 focus:ring-primary focus:border-primary outline-none transition-all" placeholder="e.g. 21201" value={formData.zipCode} onChange={e => setFormData({
                ...formData,
                zipCode: e.target.value
              })} required data-id="element-327" />
                </div>
              </div>}

            {/* STEP 2 */}
            {step === 2 && <div className="space-y-4" data-id="element-328">
                <h4 className="text-lg font-semibold text-slate-800 mb-4" data-id="element-329">
                  What services do you need?
                </h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-330">
                  {[{
                id: 'internet',
                label: 'Internet',
                icon: WifiIcon
              }, {
                id: 'internet-phone',
                label: 'Internet + Phone',
                icon: PhoneIcon
              }, {
                id: 'internet-phone-tv',
                label: 'Internet + Phone + TV',
                icon: TvIcon
              }, {
                id: 'internet-ucaas',
                label: 'Internet + UCaaS',
                icon: MonitorSmartphoneIcon
              }].map(service => {
                const isSelected = formData.services.includes(service.id);
                const Icon = service.icon;
                return <div key={service.id} onClick={() => handleServiceToggle(service.id)} className={`cursor-pointer border-2 rounded-xl p-4 flex flex-col items-center text-center transition-all ${isSelected ? 'border-primary bg-primary/5' : 'border-slate-200 hover:border-primary/30'}`} data-id="element-331">
                        <Icon className={`w-8 h-8 mb-2 ${isSelected ? 'text-primary' : 'text-slate-400'}`} data-id="element-332" />
                        <span className={`font-medium text-sm ${isSelected ? 'text-primary' : 'text-slate-700'}`} data-id="element-333">
                          {service.label}
                        </span>
                      </div>;
              })}
                </div>
              </div>}

            {/* STEP 3 */}
            {step === 3 && <div className="space-y-4" data-id="element-334">
                <h4 className="text-lg font-semibold text-slate-800 mb-4" data-id="element-335">
                  How many users?
                </h4>
                <div className="grid grid-cols-2 gap-3" data-id="element-336">
                  {['1-5 users', '6-10 users', '11-15 users', '16-20 users', '20+ users'].map(size => {
                const isSelected = formData.users === size;
                return <div key={size} onClick={() => setFormData({
                  ...formData,
                  users: size
                })} className={`cursor-pointer border-2 rounded-xl p-3 flex items-center transition-all ${isSelected ? 'border-primary bg-primary/5' : 'border-slate-200 hover:border-primary/30'}`} data-id="element-337">
                        <div className={`w-4 h-4 rounded-full border flex items-center justify-center mr-3 ${isSelected ? 'border-primary' : 'border-slate-300'}`} data-id="element-338">
                          {isSelected && <div className="w-2 h-2 rounded-full bg-primary" data-id="element-339"></div>}
                        </div>
                        <UsersIcon className={`w-5 h-5 mr-2 ${isSelected ? 'text-primary' : 'text-slate-400'}`} data-id="element-340" />
                        <span className={`font-medium text-sm ${isSelected ? 'text-primary' : 'text-slate-700'}`} data-id="element-341">
                          {size}
                        </span>
                      </div>;
              })}
                </div>
              </div>}

            {/* STEP 4 */}
            {step === 4 && <div className="space-y-4" data-id="element-342">
                <h4 className="text-lg font-semibold text-slate-800 mb-4" data-id="element-343">
                  Contact Information
                </h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-344">
                  <div data-id="element-345">
                    <label className="block text-xs font-medium text-slate-700 mb-1" data-id="element-346">
                      Full Name
                    </label>
                    <input type="text" required className="w-full px-3 py-2 rounded-lg border border-slate-300 focus:ring-2 focus:ring-primary focus:border-primary outline-none" value={formData.fullName} onChange={e => setFormData({
                  ...formData,
                  fullName: e.target.value
                })} data-id="element-347" />
                  </div>
                  <div data-id="element-348">
                    <label className="block text-xs font-medium text-slate-700 mb-1" data-id="element-349">
                      Organization
                    </label>
                    <input type="text" required className="w-full px-3 py-2 rounded-lg border border-slate-300 focus:ring-2 focus:ring-primary focus:border-primary outline-none" value={formData.organization} onChange={e => setFormData({
                  ...formData,
                  organization: e.target.value
                })} data-id="element-350" />
                  </div>
                  <div data-id="element-351">
                    <label className="block text-xs font-medium text-slate-700 mb-1" data-id="element-352">
                      Email Address
                    </label>
                    <input type="email" required className="w-full px-3 py-2 rounded-lg border border-slate-300 focus:ring-2 focus:ring-primary focus:border-primary outline-none" value={formData.email} onChange={e => setFormData({
                  ...formData,
                  email: e.target.value
                })} data-id="element-353" />
                  </div>
                  <div data-id="element-354">
                    <label className="block text-xs font-medium text-slate-700 mb-1" data-id="element-355">
                      Phone Number
                    </label>
                    <input type="tel" required className="w-full px-3 py-2 rounded-lg border border-slate-300 focus:ring-2 focus:ring-primary focus:border-primary outline-none" value={formData.phone} onChange={e => setFormData({
                  ...formData,
                  phone: e.target.value
                })} data-id="element-356" />
                  </div>
                  <div className="sm:col-span-2" data-id="element-357">
                    <label className="block text-xs font-medium text-slate-700 mb-1" data-id="element-358">
                      Additional Information
                    </label>
                    <textarea rows={2} className="w-full px-3 py-2 rounded-lg border border-slate-300 focus:ring-2 focus:ring-primary focus:border-primary outline-none resize-none" value={formData.additionalInfo} onChange={e => setFormData({
                  ...formData,
                  additionalInfo: e.target.value
                })} data-id="element-359"></textarea>
                  </div>
                </div>
              </div>}
          </motion.div>
        </AnimatePresence>

        <div className="mt-8 flex justify-between items-center pt-4 border-t border-slate-100" data-id="element-360">
          {step > 1 ? <button type="button" onClick={handleBack} className="text-slate-500 hover:text-slate-800 font-medium px-4 py-2 transition-colors" data-id="element-361">
              Back
            </button> : <div data-id="element-362"></div>}

          {step < 4 ? <button type="button" onClick={handleNext} disabled={step === 1 && !formData.zipCode} className="bg-primary hover:bg-primary-dark disabled:opacity-50 disabled:cursor-not-allowed text-white px-8 py-2.5 rounded-full font-medium transition-colors ml-auto" data-id="element-363">
              Next Step
            </button> : <button type="submit" className="w-full bg-accent hover:bg-accent-dark text-white font-bold py-4 rounded-xl transition-all shadow-lg shadow-accent/20 flex items-center justify-center gap-2 group" data-id="element-364">
              Get My Free Audit
              <ArrowRightIcon className="w-5 h-5 group-hover:translate-x-1 transition-transform" data-id="element-365" />
            </button>}
        </div>
      </form>
    </div>;
}